import type {
  ListRecordsResponse,
  SingleRecordResponse,
} from '@stakeholderlabs/types'
import type { KscpResponse } from '~/types'
import type { Alert, EmailAlertContent } from '~/types/models/alert'
import type { Filing } from '~/types/models/filing'
import type { HistoricalStock } from '~/types/models/historical-stock'
import type { PressRelease } from '~/types/models/press-release'
import type { RealTimeStock } from '~/types/models/real-time-stock'
import { useApiGet } from '~/composables/api/useBaseFetch'

export const useKaleidoscopeApi = () => {
  const search = (ticker: string, params: Filing.SearchRequest = {}) =>
    useApiGet<KscpResponse<Filing.Model[]>>(`/kaleidoscope/v1/sec/search/${ticker}`, { params })

  const getForms = () =>
    useApiGet<KscpResponse<Filing.Form[]>>('/kaleidoscope/v1/sec/listvalues/form', { cache: 'no-cache' })

  const getPressReleases = (ticker: string, params: PressRelease.RequestParams = {}) =>
    useApiGet<KscpResponse<PressRelease.Model[]>>(`/kaleidoscope/v1/news/press-releases/${ticker}`, { params })

  const getPressReleaseById = (id: number) =>
    useApiGet<KscpResponse<PressRelease.Model>>(`/kaleidoscope/v1/news/press-releases/item/${id}`)

  /** Email alerts use GET per upstream contract; do not migrate to POST/DELETE. */
  const addEmailAlert = (email: string, content: EmailAlertContent) =>
    useApiGet<KscpResponse<Alert.Model>>('/kaleidoscope/v1/sec/alerts', {
      params: { action: 'add', email_addr: email, content },
    })

  const deleteEmailAlert = (email: string, content: EmailAlertContent) =>
    useApiGet<KscpResponse<Alert.Model>>('/kaleidoscope/v1/sec/alerts', {
      params: { action: 'delete', email_addr: email, content },
    })

  const getRealTimeTicker = () =>
    useApiGet<SingleRecordResponse<RealTimeStock.Model>>('/kaleidoscope/stocks/real-time')

  const getHistoricalTicker = (params: HistoricalStock.RequestParams = {}) =>
    useApiGet<ListRecordsResponse<HistoricalStock.Model>>('/kaleidoscope/stocks/historical', { params })

  return {
    search,
    getForms,
    getPressReleases,
    getPressReleaseById,
    addEmailAlert,
    deleteEmailAlert,
    getRealTimeTicker,
    getHistoricalTicker,
  }
}
